import { Pipe, PipeTransform, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Local standalone ports of the four AngularJS filters the curation `ftStoryCard`
 * template (account-v1/.../sections/stories/storyCard.html) binds. Kept LOCAL +
 * standalone — NOT the shared account-shared/pipes copies — so the curation card
 * stays fully self-contained and never routes through the eval-order-fragile
 * account-shared barrel ([[project_account_barrel_eval_order_fragility]]). Each is
 * a byte-faithful port of the v1 filter the FROZEN baseline renders with, so the
 * candidate's text matches v1 character-for-character (pixel parity).
 */

/**
 * v1 `truncate` (account-v1/.../text-formatting/truncate.filter.js) — byte-identical
 * (wordwise cut at the last space ≤ maxLength, `tail || '…'`).
 */
@Pipe({ name: 'truncate', pure: true })
export class CurationTruncatePipe implements PipeTransform {
  transform(text: string, maxLength: number, wordwise = false, tail = '…'): string {
    if (!text) return '';

    maxLength = parseInt(maxLength as unknown as string, 10);
    if (!maxLength) return text;
    if (text.length <= maxLength) return text;

    if (wordwise) {
      const lastSpace = text.lastIndexOf(' ', maxLength);
      text = text.substring(0, lastSpace !== -1 ? lastSpace : maxLength);
    } else {
      text = text.substring(0, maxLength);
    }

    return text + (tail || '…');
  }
}

/**
 * v1 `fullName` (account-v1/.../core/user/fullName.filter.js) — reads `first||FirstName`
 * + `last||LastName` (NOT the lowercase variants the account-shared FullNamePipe also
 * checks — matching v1 exactly is what the frozen baseline renders). Includes the
 * 1.73.x fallback (email → CRS# → PMS#) master added to the v1 filter.
 */
@Pipe({ name: 'fullName' })
export class CurationFullNamePipe implements PipeTransform {
  transform(source: any, isInitialOnly = false): string {
    if (!source) return '';
    const first = source.first || source.FirstName;
    const last = source.last || source.LastName;
    if (!first && !last) return this.fallbackName(source);
    let name: any = first;
    if (last) name = name + ' ' + (isInitialOnly ? last.substring(0, 1) + '.' : last);
    return name;
  }

  /** Fallback label for records identified only by other PII (v1 1.73.x fallbackName). */
  private fallbackName(source: any): string {
    if (source.email || source.Email) return source.email || source.Email;
    if (source.crsNumber || source.crs_number) return 'CRS# ' + (source.crsNumber || source.crs_number);
    if (source.pmsNumber || source.pms_number) return 'PMS# ' + (source.pmsNumber || source.pms_number);
    return '';
  }
}

/**
 * v1 `quot` (account-v1/.../text-formatting/quot.filter.js) — wraps in `&quot;` HTML
 * entities and trusts as HTML (used with `[innerHTML]`, like the original's
 * `ng-bind-html`). Mirrors the people-side QuotPipe.
 */
@Pipe({ name: 'quot' })
export class CurationQuotPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);

  transform(text: string | null | undefined): SafeHtml {
    if (!text) return '';
    return this.sanitizer.bypassSecurityTrustHtml('&quot;' + text + '&quot;');
  }
}

/**
 * Equivalent of the AngularJS `amDifference : null : 'days'` (angular-moment) the
 * v1 template uses for the "N days old" badge: `moment(date).diff(moment(), 'days')`.
 * moment's day-diff truncates toward zero (absFloor); `Math.trunc(ms / 864e5)` matches
 * for two instants in the same UTC offset (the captures share one machine clock, so
 * the DST correction moment applies is 0 here). Returns a SIGNED day count (negative
 * for past dates) so the template's `… <= -1` test and `* -1` display port verbatim.
 */
@Pipe({ name: 'amDifferenceDays' })
export class DaysDifferencePipe implements PipeTransform {
  transform(date: any): number {
    if (!date) return 0;
    const then = new Date(date).getTime();
    if (isNaN(then)) return 0;
    return Math.trunc((then - Date.now()) / 86400000);
  }
}
